import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import i18next from 'i18next';
import { FormatDate } from "../../shared/utils/utils";
import { getCurrentAccount } from "../../services/authService";
import usePersistedQueryParams from "../../shared/usePersistedAppQueryParams";
import {
  getRVGWarrantyBySerialNumber,
  registerRVGWarrantyBySerialNumber,
} from "../../services/rvgRegistration";


const useStyles = makeStyles((theme) => ({
  root: {
    textAlign: "left",
    margin: "80px auto",
    borderRadius: "10px",
    boxShadow: "0 00 4px rgb(0 0 0 / 25%)",
    backgroundColor: "#ffffff",
    minHeight: "100%",
    padding: 15,
    "& > h2": {
      fontSize: "1.5rem",
    },
  },
  form: {
    display: "flex",
    flexDirection: "row",
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  formContainer: {
    margin: "20px 0 20px 0",
  },
  inlineGrid: {
    display: "inline-grid",
    width: "50%",
  },
  inlineFullWidthGrid: {
    display: "inline-grid",
    width: "100%",
  },
  button: {
    textTransform: "none",
    color: "#ffffff",
    boxShadow: "none",

    "&:hover, &:active": {
      boxShadow: "none",
    },
  },
  consentInput: {
    paddingLeft: "5px",
  },
  consentLabel: {
    padding: "5px",
    fontSize: "12px",
  },
}));

const Home: React.FC = () => {
  const classes = useStyles();
  const { serialNumber, lang } = usePersistedQueryParams();
  const [product, setProduct] = React.useState<string>("");
  const [installationDate, setInstallationDate] = React.useState<string>("");
  const [email, setEmail] = React.useState<string>("");
  const [rvgRegistred, setRvgRegistred] = React.useState<boolean>(false);
  const [acceptConsent, setAcceptConsent] = React.useState<boolean>(false);
  const [userName, setUserName] = React.useState<string>("");
  const { t } = useTranslation();


  React.useEffect(() => {
    i18next.changeLanguage(lang);

    const idTokenClaims: any = getCurrentAccount()?.idTokenClaims;
    const { email, family_name, given_name } = idTokenClaims;
    setUserName(`${given_name} ${family_name}`);
    setEmail(email);
    getRVGWarrantyBySerialNumber(serialNumber as string)
      .then((res) => {
        setInstallationDate(res?.warranty?.firstUseDate);
        setProduct(res?.warranty?.productType);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [serialNumber, lang]);

  const handleSubmit = (e: React.ChangeEvent<{}>) => {
    e.preventDefault();
    registerRVGWarrantyBySerialNumber(serialNumber as string, email, acceptConsent)
      .then((res) => {
        setRvgRegistred(true);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <Box className={classes.root} width={450}>
      <Typography variant="h2">{t("Device registration")}</Typography>
      <br />
      {!rvgRegistred ? (
        <Grid
          container
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <Grid container item>
            <Typography variant="body1" color="initial">
              {userName},
            </Typography>
            <Typography variant="body1" color="initial">
              {t("Thanks for taking the time to register a medical device,")}
            </Typography>
            <Typography variant="body1" color="initial">
              {t(
                "Carestream Dental will send you safety information if needed."
              )}
            </Typography>
          </Grid>
          <Grid
            container
            item
            justifyContent="center"
            className={classes.formContainer}
          >
            <Grid item className={classes.inlineGrid}>
              <TextField
                id="product"
                title={product}
                label={t("Product")}
                value={product}
                variant="standard"
                disabled
                InputProps={{ disableUnderline: true }}
              />
            </Grid>
            <Grid item className={classes.inlineGrid}>
              <TextField
                id="serialNumber"
                title={serialNumber as string}
                label={t("Serial Number")}
                value={serialNumber}
                variant="standard"
                disabled
                InputProps={{ disableUnderline: true }}
              />
            </Grid>
            <Grid item className={classes.inlineFullWidthGrid}>
              <TextField
                id="installationDate"
                title={FormatDate(installationDate)}
                label={t("Installation date")}
                value={FormatDate(installationDate)}
                variant="standard"
                disabled
                InputProps={{ disableUnderline: true }}
              />
            </Grid>
          </Grid>

          <Grid container item>
            <FormControlLabel
              className={classes.consentInput}
              control={
                <Checkbox
                  checked={acceptConsent}
                  onChange={() => setAcceptConsent(!acceptConsent)}
                  name="acceptConsent"
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" className={classes.consentLabel}>
                  {t(
                    "Yes, I would like to receive communications on products, services, events and special offers from Carestream Dental.  I am aware that I can withdraw my consent at any time. For any further requests please send an email to"
                  )}

                  <Link
                    href="mailto:privacy@csdental.com"
                    color="secondary"
                    style={{ marginLeft: 4 }}
                  >
                    privacy@csdental.com
                  </Link>
                </Typography>
              }
            />
            <br />
          </Grid>
          <Grid
            container
            item
            direction="row"
            justifyContent="center"
            alignItems="flex-end"
          >
            <Button type="submit" variant="contained" className={classes.button}>
              {t("Register")}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid>{t("Your device has been successfully registered.")}</Grid>
      )}
    </Box>
  );
};

export default Home;
